import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { PasswordInput } from "@/components/common/PasswordInput";
import { useAuth, apiError } from "@/context/AuthContext";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [remember, setRemember] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true); setError("");
        try {
            const user = await login(email, password, remember);
            toast.success("Welcome back, operator.");
            navigate(user.role === "admin" ? "/choose" : "/dashboard");
        } catch (err) {
            const msg = apiError(err.response?.data?.detail) || err.message;
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout title="Sign in" subtitle="Access your tactical command center.">
            <form onSubmit={submit} className="space-y-5" data-testid="login-form">
                <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                    <Input data-testid="login-email" type="email" required value={email}
                        onChange={(e) => setEmail(e.target.value)} placeholder="coach@whitewater.ai"
                        className="bg-surface border-border rounded-sm h-11" />
                </div>
                <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
                    <PasswordInput testid="login-password" value={password} autoComplete="current-password"
                        onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
                </div>
                <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                        <Checkbox data-testid="remember-me" checked={remember} onCheckedChange={setRemember} className="rounded-sm data-[state=checked]:bg-volt data-[state=checked]:border-volt" />
                        Remember me
                    </label>
                    <Link to="/forgot-password" className="text-sm text-volt hover:underline" data-testid="forgot-link">Forgot password?</Link>
                </div>
                {error && <div className="text-sm text-alert font-mono" data-testid="login-error">{error}</div>}
                <button data-testid="login-submit" disabled={loading} type="submit"
                    className="w-full h-11 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
                </button>
                <p className="text-sm text-muted-foreground text-center">
                    New here? <Link to="/register" className="text-volt hover:underline" data-testid="register-link">Create an account</Link>
                </p>
            </form>
        </AuthLayout>
    );
}
